import { useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { socketManager } from 'utils/socketManager';
import { meApi } from 'store/services/user/meApi';
import { profileApi } from 'store/services/profile/profileApi';
import {
  queuePointsChange,
  pointsSocketConnecting,
  pointsSocketConnected,
  pointsSocketDisconnected,
  pointsSocketError,
  pointsSocketReconnectAttempt,
  selectPointsConnectionStatus,
} from 'store/slices/pointsSlice';
import { PointsUpdatePayload } from 'types/interfaces/points';
import { showPointsToast } from 'utils/pointsToastNotifications';

interface UsePointsSocketOptions {
  enabled?: boolean;
  onPointsUpdate?: (payload: PointsUpdatePayload) => void;
}

interface UsePointsSocketReturn {
  isConnected: boolean;
  isConnecting: boolean;
  connectionError: string | null;
}

const POLLING_INTERVAL = 30000; // 30 seconds

/**
 * Hook to manage Socket.IO connection for points updates.
 * Uses shared socket manager singleton.
 */
export const usePointsSocket = (
  options: UsePointsSocketOptions = {}
): UsePointsSocketReturn => {
  const { enabled = true, onPointsUpdate } = options;
  const dispatch = useDispatch();
  const hasAcquiredRef = useRef(false);

  const { isConnected, isConnecting, connectionError } = useSelector(
    (state: RootState) => selectPointsConnectionStatus(state)
  );

  // Stable callback ref to avoid effect re-runs
  const onPointsUpdateRef = useRef(onPointsUpdate);
  useEffect(() => {
    onPointsUpdateRef.current = onPointsUpdate;
  }, [onPointsUpdate]);

  // Handle incoming points update events
  const handlePointsUpdate = useCallback(
    (payload: PointsUpdatePayload) => {
      console.log('[PointsSocket] Received points:update', payload);

      // 1. Queue animation in Redux
      dispatch(queuePointsChange(payload));

      // 2. Update RTK Query cache immediately
      // Using any type for dispatch to match existing pattern in activityApi
      (dispatch as any)(
        meApi.util.updateQueryData('getMe', undefined, (draft) => {
          if (draft) {
            draft.points = payload.newBalance;
          }
        })
      );

      // 3. Invalidate public profile cache (updates profile page if viewing own profile)
      (dispatch as any)(
        profileApi.util.invalidateTags(['PublicProfile'])
      );

      // 4. Show toast notification (with batching)
      showPointsToast(payload);

      // 5. Call optional callback
      if (onPointsUpdateRef.current) {
        onPointsUpdateRef.current(payload);
      }
    },
    [dispatch]
  );

  // Connection state handler
  const handleConnectionChange = useCallback(
    (connected: boolean) => {
      if (connected) {
        dispatch(pointsSocketConnected());
      } else {
        dispatch(pointsSocketDisconnected());
      }
    },
    [dispatch]
  );

  // Main socket effect
  useEffect(() => {
    if (!enabled) {
      return;
    }

    dispatch(pointsSocketConnecting());
    hasAcquiredRef.current = true;

    const socket = socketManager.acquireSocket();

    // Subscribe to connection state changes
    socketManager.addConnectionListener(handleConnectionChange);

    // Check current connection state
    if (socket.connected) {
      dispatch(pointsSocketConnected());
    }

    // Subscribe to socket events
    const onConnect = () => {
      console.log('[PointsSocket] Connected');
      dispatch(pointsSocketConnected());
    };

    const onDisconnect = (reason: string) => {
      console.log('[PointsSocket] Disconnected:', reason);
      dispatch(pointsSocketDisconnected());
    };

    const onConnectError = (error: Error) => {
      console.error('[PointsSocket] Connection error:', error.message);
      dispatch(pointsSocketError(error.message));
    };

    const onReconnectAttempt = (attemptNumber: number) => {
      console.log('[PointsSocket] Reconnect attempt:', attemptNumber);
      dispatch(pointsSocketReconnectAttempt());
    };

    const onReconnectFailed = () => {
      console.error('[PointsSocket] Reconnection failed after max attempts');
      dispatch(pointsSocketError('Reconnection failed'));
    };

    // Register connection event handlers
    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);
    socket.on('connect_error', onConnectError);
    socket.on('reconnect_attempt', onReconnectAttempt);
    socket.on('reconnect_failed', onReconnectFailed);

    // Points update event handler
    socket.on('points:update', handlePointsUpdate);

    // Cleanup on unmount
    return () => {
      console.log('[PointsSocket] Cleaning up');
      socket.off('connect', onConnect);
      socket.off('disconnect', onDisconnect);
      socket.off('connect_error', onConnectError);
      socket.off('reconnect_attempt', onReconnectAttempt);
      socket.off('reconnect_failed', onReconnectFailed);
      socket.off('points:update', handlePointsUpdate);
      socketManager.removeConnectionListener(handleConnectionChange);

      if (hasAcquiredRef.current) {
        socketManager.releaseSocket();
        hasAcquiredRef.current = false;
      }
    };
  }, [enabled, dispatch, handlePointsUpdate, handleConnectionChange]);

  // Fallback polling when disconnected
  useEffect(() => {
    if (!enabled || isConnected) {
      return;
    }

    console.log('[PointsSocket] Disconnected - starting fallback polling');

    const pollInterval = setInterval(() => {
      // Invalidate the meApi cache to trigger a refetch
      dispatch(meApi.util.invalidateTags(['User']));
    }, POLLING_INTERVAL);

    return () => {
      clearInterval(pollInterval);
    };
  }, [enabled, isConnected, dispatch]);

  return {
    isConnected,
    isConnecting,
    connectionError,
  };
};

export default usePointsSocket;
